import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import FieldSelect from "./FieldSelect";
import { HiddenColumnsNote, toastApiError, useMountedRef } from "./common";
import "./Import.css";

// "<Tab> › <Label>" for a resolved field id, or "" when it isn't in the catalog.
function targetText(field) {
	if (!field) return "";
	return field.tabName ? `${field.tabName} › ${field.label}` : field.label;
}

// "Contacts › First Name / Last Name" for a full-name column's {first, last}
// split, or "" when the catalog doesn't know those ids.
function splitTargetText(split, fieldById) {
	if (!split) return "";
	const first = fieldById[String(split.first)];
	const last = fieldById[String(split.last)];
	const labels = [first, last].filter(Boolean).map((f) => f.label);
	if (labels.length === 0) return "";
	const tabName = (first || last).tabName;
	const names = labels.join(" / ");
	return tabName ? `${tabName} › ${names}` : names;
}

function sampleValue(sampleRows, columns, col) {
	for (const row of sampleRows || []) {
		let v;
		if (Array.isArray(row)) {
			const idx = columns.indexOf(col);
			v = idx >= 0 ? row[idx] : undefined;
		} else if (row && typeof row === "object") {
			v = row[col];
		}
		if (v !== undefined && v !== null && String(v).trim() !== "") {
			return String(v);
		}
	}
	return "";
}

export default function MapColumnsStep({
	api,
	columns,
	hidden = { scrubInfo: [], empty: [] }, // never-import columns, for the collapsed note
	sampleRows,
	columnStats = {}, // {column: {filled, total, examples}} over the whole file
	mapping,
	onSetField,
	onSetContactField,
	onClearContactSplit,
	onSetScope,
	fields,
	catalogStatus, // "idle" | "loading" | "ready" | "error"
	onRetryCatalog,
	groupBy,
	onApplyTemplate,
	onBack,
	onNext,
}) {
	const mountedRef = useMountedRef();
	const [templates, setTemplates] = useState([]);
	const [tplLoading, setTplLoading] = useState(false);
	const [tplSelected, setTplSelected] = useState("");
	const [tplName, setTplName] = useState("");
	const [tplSaving, setTplSaving] = useState(false);

	const mappedCount = Object.keys(mapping).length;

	const fieldById = useMemo(() => {
		const out = {};
		fields.forEach((f) => {
			out[String(f.id)] = f;
		});
		return out;
	}, [fields]);

	// Candidates for the paired "Contacts record field": the Contacts record
	// set, or any record-set field when no tab is named Contacts.
	const contactFields = useMemo(() => {
		const recordSet = fields.filter((f) => f.isRecordSet);
		const contacts = recordSet.filter((f) =>
			String(f.tabName || "").toLowerCase().includes("contact")
		);
		return contacts.length > 0 ? contacts : recordSet;
	}, [fields]);

	const refreshTemplates = async () => {
		setTplLoading(true);
		try {
			const data = await api.listMappings();
			if (!mountedRef.current) return;
			setTemplates(Array.isArray(data) ? data : data?.mappings || []);
		} catch (err) {
			toastApiError(err, "Failed to load mapping templates");
		} finally {
			if (mountedRef.current) setTplLoading(false);
		}
	};

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setTplLoading(true);
			try {
				const data = await api.listMappings();
				if (cancelled) return;
				setTemplates(Array.isArray(data) ? data : data?.mappings || []);
			} catch (err) {
				if (!cancelled) toastApiError(err, "Failed to load mapping templates");
			} finally {
				if (!cancelled) setTplLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [api]);

	const loadTemplate = () => {
		const tpl = templates.find((t) => String(t.id) === String(tplSelected));
		if (!tpl) {
			toast.warn("Pick a template to load first.");
			return;
		}
		onApplyTemplate(tpl);
	};

	const deleteTemplate = async () => {
		const tpl = templates.find((t) => String(t.id) === String(tplSelected));
		if (!tpl) {
			toast.warn("Pick a template to delete first.");
			return;
		}
		// Template deletion only removes a saved mapping — native confirm is enough.
		// eslint-disable-next-line no-restricted-globals
		if (!window.confirm(`Delete mapping template "${tpl.name}"?`)) return;
		try {
			await api.deleteMapping(tpl.id);
			toast.success(`Template "${tpl.name}" deleted.`);
			if (mountedRef.current) setTplSelected("");
			await refreshTemplates();
		} catch (err) {
			toastApiError(err, "Failed to delete template");
		}
	};

	const saveTemplate = async () => {
		const name = tplName.trim();
		if (!name) {
			toast.warn("Give the template a name first.");
			return;
		}
		if (mappedCount === 0) {
			toast.warn("Map at least one column before saving a template.");
			return;
		}
		setTplSaving(true);
		try {
			await api.saveMapping({ name, mapping, group_by: groupBy });
			toast.success(`Template "${name}" saved.`);
			if (mountedRef.current) setTplName("");
			await refreshTemplates();
		} catch (err) {
			toastApiError(err, "Failed to save template");
		} finally {
			if (mountedRef.current) setTplSaving(false);
		}
	};

	return (
		<div>
			<h2>🗂 Map Columns to Iris Fields</h2>
			<p style={{ marginBottom: 12 }}>
				Every CSV column gets an Iris field (or is ignored), shown as{" "}
				<b>Tab › Field</b>. Auto-suggestions only pre-fill — everything here is
				editable. A column with a <b>Contacts record field</b> lands in both
				places (the main contact's row on the company record, every row as a
				contact record).
			</p>

			{/* Template load/save bar */}
			<div className="templateBar">
				<span style={{ fontWeight: 600 }}>Templates:</span>
				<select
					className="importSelect"
					value={tplSelected}
					onChange={(e) => setTplSelected(e.target.value)}
					disabled={tplLoading || templates.length === 0}>
					<option value="">
						{tplLoading
							? "Loading…"
							: templates.length === 0
							? "No saved templates"
							: "Select a template…"}
					</option>
					{templates.map((t) => (
						<option key={t.id} value={t.id}>
							{t.name}
						</option>
					))}
				</select>
				<button
					type="button"
					className="importBtn secondary small"
					onClick={loadTemplate}
					disabled={!tplSelected}>
					Load
				</button>
				<button
					type="button"
					className="importBtn secondary small"
					onClick={deleteTemplate}
					disabled={!tplSelected}>
					Delete
				</button>
				<span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
					<input
						className="importInput"
						placeholder="New template name…"
						value={tplName}
						onChange={(e) => setTplName(e.target.value)}
					/>
					<button
						type="button"
						className="importBtn secondary small"
						onClick={saveTemplate}
						disabled={tplSaving}>
						{tplSaving ? "Saving…" : "Save current"}
					</button>
				</span>
			</div>

			{catalogStatus === "loading" && (
				<p className="importMuted">🔄 Loading Iris field catalog…</p>
			)}
			{catalogStatus === "error" && (
				<div className="resultBanner error">
					Failed to load the Iris field catalog.{" "}
					<button
						type="button"
						className="importBtn secondary small"
						onClick={onRetryCatalog}>
						Retry
					</button>
				</div>
			)}

			{catalogStatus === "ready" && fields.length === 0 && (
				<div className="resultBanner error">
					The Iris field catalog came back empty — mapping is not possible.
					Check the backend / Iris connectivity.
				</div>
			)}

			{catalogStatus === "ready" && fields.length > 0 && (
				<div className="importTableWrap" style={{ overflow: "visible" }}>
					<table className="importTable">
						<thead>
							<tr>
								<th>CSV column</th>
								<th>Sample value</th>
								<th>Iris field</th>
								<th>Contacts record field</th>
								<th>
									Where it lands
									<div
										style={{
											fontWeight: 400,
											fontSize: 11,
											color: "#718096",
											whiteSpace: "normal",
											maxWidth: 230,
											marginTop: 2,
										}}>
										<b>Company record</b> = writes to Business Information from
										the main contact's row (and to each contact when a Contacts
										field is set). <b>Each contact only</b> = writes just to the
										individual contact rows.
									</div>
								</th>
							</tr>
						</thead>
						<tbody>
							{columns.map((col) => {
								const entry = mapping[col];
								const contactFieldId = entry ? entry.contact_field_id : null;
								const pairedField =
									contactFieldId === null || contactFieldId === undefined
										? null
										: fieldById[String(contactFieldId)];
								const isGuess = entry && entry.via === "weak_label";
								// A full-name column feeds the Contacts set's separate
								// First/Last Name fields on every row instead of a single
								// paired field (read-only here; ✕ opts out).
								const splitText =
									entry && entry.contact_split
										? splitTargetText(entry.contact_split, fieldById)
										: "";
								const alsoEachContact =
									entry && entry.scope === "lead" && (pairedField || splitText);
								return (
									<tr key={col}>
										<td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
											{col}
										</td>
										<td
											className="importMuted"
											style={{
												maxWidth: 180,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
											title={sampleValue(sampleRows, columns, col)}>
											{sampleValue(sampleRows, columns, col) ||
												(columnStats[col]?.examples || [])[0] ||
												"—"}
											{columnStats[col] && (
												<div
													style={{
														fontSize: 11,
														fontWeight: columnStats[col].filled ? 400 : 600,
														color: columnStats[col].filled ? "#718096" : "#C05621",
													}}>
													{columnStats[col].filled
														? `${columnStats[col].filled} of ${columnStats[col].total} rows filled`
														: "Empty in every row — nothing to import"}
												</div>
											)}
										</td>
										<td>
											<FieldSelect
												fields={fields}
												value={entry ? entry.field_id : null}
												onChange={(fieldId) => onSetField(col, fieldId)}
											/>
											{pairedField && (
												<div className="mapPairedHint">
													+ {targetText(pairedField)} (all rows)
												</div>
											)}
											{isGuess && (
												<div className="mapGuessHint">
													⚠️ guess — please verify
												</div>
											)}
										</td>
										<td>
											{splitText ? (
												<div
													style={{
														display: "flex",
														alignItems: "flex-start",
														gap: 6,
													}}>
													<span
														className="mapPairedHint"
														style={{ whiteSpace: "normal", marginTop: 0 }}>
														+ {splitText} (split from full name, all rows)
													</span>
													<button
														type="button"
														className="importBtn secondary small"
														title="Turn off the name split — this column then has no contact-side target"
														onClick={() => onClearContactSplit(col)}>
														✕
													</button>
												</div>
											) : entry && entry.scope === "lead" ? (
												<FieldSelect
													fields={contactFields}
													value={contactFieldId ?? null}
													emptyLabel="— none —"
													onChange={(fieldId) =>
														onSetContactField(col, fieldId)
													}
												/>
											) : (
												<span className="importMuted" style={{ fontSize: 12 }}>
													{entry
														? "— maps to a contact record already —"
														: "—"}
												</span>
											)}
										</td>
										<td>
											<div className="scopeToggle">
												<button
													type="button"
													className={
														entry && entry.scope === "lead" ? "active" : ""
													}
													disabled={!entry}
													onClick={() => onSetScope(col, "lead")}>
													Company record
												</button>
												<button
													type="button"
													className={
														entry && entry.scope === "contact" ? "active" : ""
													}
													disabled={!entry}
													onClick={() => onSetScope(col, "contact")}>
													Each contact only
												</button>
											</div>
											{alsoEachContact && (
												<div className="mapPairedHint">+ each contact</div>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			<HiddenColumnsNote hidden={hidden} />

			<div className="wizardNav">
				<button type="button" className="importBtn secondary" onClick={onBack}>
					← Back
				</button>
				<span className="wizardNavHint">
					{mappedCount === 0
						? "Map at least one column to continue."
						: `${mappedCount} of ${columns.length} columns mapped.`}
				</span>
				<button
					type="button"
					className="importBtn"
					disabled={mappedCount === 0}
					onClick={onNext}>
					Continue to Grouping →
				</button>
			</div>
		</div>
	);
}
